import type { GameLoopStepClock } from '../game/GameLoop';

export interface System {
  readonly name: string;
  init?(services: ServiceLocator): Promise<void> | void;
  tickFixed?(dt: number, clock: GameLoopStepClock): void;
  tickRender?(dt: number, clock: GameLoopStepClock): void;
  dispose?(): void;
}

type SystemCtor<T extends System> = new (...args: never[]) => T;

export class ServiceLocator {
  private services = new Map<SystemCtor<System>, System>();
  private order: SystemCtor<System>[] = [];

  register<T extends System>(ctor: SystemCtor<T>, instance: T): T {
    if (this.services.has(ctor)) {
      throw new Error(`Service already registered: ${ctor.name}`);
    }
    this.services.set(ctor, instance);
    this.order.push(ctor);
    return instance;
  }

  get<T extends System>(ctor: SystemCtor<T>): T {
    const sys = this.services.get(ctor);
    if (!sys) throw new Error(`Service not registered: ${ctor.name}`);
    return sys as T;
  }

  has<T extends System>(ctor: SystemCtor<T>): boolean {
    return this.services.has(ctor);
  }

  async initAll(): Promise<void> {
    for (const ctor of this.order) {
      const sys = this.services.get(ctor)!;
      if (sys.init) await sys.init(this);
    }
  }

  tickFixed(dt: number, clock: GameLoopStepClock): void {
    for (const ctor of this.order) {
      this.services.get(ctor)!.tickFixed?.(dt, clock);
    }
  }

  tickRender(dt: number, clock: GameLoopStepClock): void {
    for (const ctor of this.order) {
      this.services.get(ctor)!.tickRender?.(dt, clock);
    }
  }

  dispose(): void {
    for (let i = this.order.length - 1; i >= 0; i--) {
      this.services.get(this.order[i])!.dispose?.();
    }
  }
}
